import { useState } from 'react';
import { cabinet } from '../../api/cabinet';
import type { CaseDocument } from '../../api/types';
import { DownloadIcon } from '../../components/Icons';
import { useI18n, useT } from '../../i18n';
import { describeError } from '../../lib/errors';
import { formatDate } from '../../lib/format';
import { docTitle } from './model';

/**
 * Get a short-lived URL and open it. A window is opened synchronously on click so that
 * mobile Safari does not treat the later navigation as a pop-up.
 */
export async function openDocument(id: string, getUrl: (id: string) => Promise<{ url: string }> = cabinet.downloadUrl) {
  let w: Window | null = null;
  try {
    w = window.open('', '_blank');
    if (w) w.opener = null;
  } catch {
    w = null;
  }
  try {
    const { url } = await getUrl(id);
    if (w) w.location.href = url;
    else window.location.assign(url);
  } catch (e) {
    w?.close();
    throw e;
  }
}

function DocRow({ doc }: { doc: CaseDocument }) {
  const t = useT();
  const locale = useI18n((s) => s.locale);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const title = docTitle(doc, locale);
  return (
    <li className="row py-4">
      <div className="flex items-center gap-4">
        <div className="min-w-0 flex-1">
          <p className="text-[16px] leading-snug">{title}</p>
          <p className="mt-1 truncate text-[13px] text-lab">
            {title !== doc.file_name ? `${doc.file_name} · ` : ''}
            {formatDate(doc.created_at, locale, false)}
          </p>
        </div>
        <button
          type="button"
          className="btn-quiet flex-none"
          disabled={busy}
          aria-label={`${t('docs.download')}: ${title}`}
          onClick={() => {
            setBusy(true);
            setError(null);
            openDocument(doc.id)
              .catch(setError)
              .finally(() => setBusy(false));
          }}
        >
          <DownloadIcon />
          <span className="hidden min-[400px]:inline">{busy ? t('docs.opening') : t('docs.download')}</span>
        </button>
      </div>
      {error ? (
        <p role="alert" className="mt-2 text-[14px] text-bad">
          {t(describeError(error))}
        </p>
      ) : null}
    </li>
  );
}

export function DocumentsSection({ fromUs, yours }: { fromUs: CaseDocument[]; yours: CaseDocument[] }) {
  const t = useT();
  return (
    <section aria-labelledby="docs-h" id="documents" className="scroll-mt-6">
      <h2 id="docs-h" className="sec-h">
        {t('docs.fromUs')}
        {fromUs.length ? <small>{fromUs.length}</small> : null}
      </h2>
      {fromUs.length === 0 ? <p className="mt-5 text-[16px] text-txt">{t('docs.empty')}</p> : null}
      <ul className="mt-2">
        {fromUs.map((d) => (
          <DocRow key={d.id} doc={d} />
        ))}
      </ul>
      {yours.length ? (
        <details className="mt-6 group">
          <summary className="flex min-h-tap cursor-pointer list-none items-center justify-between text-[15px] text-txt hover:text-fg">
            <span>
              {t('docs.yours')} · {yours.length}
            </span>
            <span aria-hidden className="text-lab transition-transform group-open:rotate-45">
              +
            </span>
          </summary>
          <ul>
            {yours.map((d) => (
              <DocRow key={d.id} doc={d} />
            ))}
          </ul>
        </details>
      ) : null}
    </section>
  );
}
